/**
 * Reports Hook
 *
 * Main hook for Reports feature using clean architecture
 */

import {useCallback} from 'react';
import {useReportsStore} from '../store/reportsStore';
import {ReportsFactory} from '../store/reportsFactory';
import {ReportFilters, ExportRequest} from '../models/report.model';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';

export const useReports = () => {
  const store = useReportsStore();

  // Get marketers
  const getMarketers = useCallback(async () => {
    try {
      store.setLoadingMarketers(true);
      store.setError(null);

      const useCase = ReportsFactory.getGetMarketersUseCase();
      const marketers = await useCase.execute();

      store.setMarketers(marketers);
    } catch (error) {
      const errorHandlingService = StandardizedErrorHandler.getInstance();
      const appError = errorHandlingService.toAppError(error);
      store.setError(appError.message);
      errorHandlingService.handleError(error); // For notifications/logging
      throw appError; // Re-throw for error context
    } finally {
      store.setLoadingMarketers(false);
    }
  }, []); // Remove store dependency to prevent infinite loops

  // Get marketer stores report
  const getMarketerStoresReport = useCallback(async (marketerId: number, filters?: ReportFilters) => {
    try {
      store.setLoadingStoresReport(true);
      store.setError(null);

      const useCase = ReportsFactory.getGetMarketerStoresReportUseCase();
      const report = await useCase.execute(marketerId, filters);

      store.setStoresReport(report);
    } catch (error) {
      const errorHandlingService = StandardizedErrorHandler.getInstance();
      const appError = errorHandlingService.toAppError(error);
      store.setError(appError.message);
      errorHandlingService.handleError(error); // For notifications/logging
      throw appError;
    } finally {
      store.setLoadingStoresReport(false);
    }
  }, []); // Remove store dependency to prevent infinite loops

  // Get marketer financial report
  const getMarketerFinancialReport = useCallback(async (marketerId: number, filters?: ReportFilters) => {
    try {
      store.setLoadingFinancialReport(true);
      store.setError(null);

      const useCase = ReportsFactory.getGetMarketerFinancialReportUseCase();
      const report = await useCase.execute(marketerId, filters);

      store.setFinancialReport(report);
    } catch (error) {
      const errorHandlingService = StandardizedErrorHandler.getInstance();
      const appError = errorHandlingService.toAppError(error);
      store.setError(appError.message);
      errorHandlingService.handleError(error); // For notifications/logging
      throw appError;
    } finally {
      store.setLoadingFinancialReport(false);
    }
  }, []); // Remove store dependency to prevent infinite loops

  // Get marketer stock report
  const getMarketerStockReport = useCallback(async (marketerId: number, filters?: ReportFilters) => {
    try {
      store.setLoadingStockReport(true);
      store.setError(null);
      const useCase = ReportsFactory.getGetMarketerStockReportUseCase();
      const report = await useCase.execute(marketerId, filters);
      store.setStockReport(report);
    } catch (error) {
      const errorHandlingService = StandardizedErrorHandler.getInstance();
      const appError = errorHandlingService.toAppError(error);
      store.setError(appError.message);
      errorHandlingService.handleError(error); // For notifications/logging
      throw appError;
    } finally {
      store.setLoadingStockReport(false);
    }
  }, []);

  // Export marketer report
  const exportMarketerReport = useCallback(async (marketerId: number, request: ExportRequest) => {
    try {
      store.setExporting(true);
      store.setError(null);

      const useCase = ReportsFactory.getExportMarketerReportUseCase();
      const result = await useCase.execute(marketerId, request);

      store.setExportResponse(result as any);
      return result;
    } catch (error) {
      const errorHandlingService = StandardizedErrorHandler.getInstance();
      const appError = errorHandlingService.toAppError(error);
      store.setError(appError.message);
      errorHandlingService.handleError(error); // For notifications/logging
      throw appError;
    } finally {
      store.setExporting(false);
    }
  }, []); // Remove store dependency to prevent infinite loops

  // Clear error
  const clearError = useCallback(() => {
    store.setError(null);
  }, []); // Remove store dependency to prevent infinite loops

  // Reset store
  const reset = useCallback(() => {
    store.reset();
  }, []); // Remove store dependency to prevent infinite loops

  return {
    // State
    ...store,

    // Actions
    getMarketers,
    getMarketerStoresReport,
    getMarketerFinancialReport,
    getMarketerStockReport,
    exportMarketerReport,
    clearError,
    reset
  };
};
